import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Eye, EyeOff, AlertCircle, CheckCircle, Search, X } from 'lucide-react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  success?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  fullWidth?: boolean;
}

const inputVariants = {
  default: [
    'border border-gray-300 bg-white',
    'focus:border-primary focus:ring-1 focus:ring-primary transition-[border-color,box-shadow] duration-150',
  ],
  filled: [
    'border-0 bg-gray-100',
    'focus:bg-white focus:ring-2 focus:ring-primary',
  ],
  outlined: [
    'border-2 border-gray-300 bg-transparent',
    'focus:border-primary',
  ],
};

const inputSizes = {
  sm: 'h-8 px-3 text-base',
  md: 'h-10 px-4 text-base',
  lg: 'h-12 px-4 text-base sm:text-lg',
};

const baseInputStyles = [
  'w-full rounded-md font-apple',
  'text-gray-900 placeholder-gray-500',
  'transition-[color,background-color] duration-200 ease-apple',
  'focus:outline-none',
  'disabled:opacity-50 disabled:cursor-not-allowed',
];

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      helperText,
      leftIcon,
      rightIcon,
      size = 'md',
      variant = 'default',
      fullWidth = true,
      type = 'text',
      className,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && isPasswordVisible ? 'text' : type;

    const hasError = Boolean(error);
    const hasSuccess = Boolean(success);
    const hasLeftIcon = Boolean(leftIcon);
    const hasRightIcon = Boolean(rightIcon) || isPassword || hasError || hasSuccess;

    return (
      <div className={cn('flex flex-col space-y-1 relative', !fullWidth && 'w-auto')}>
        {/* Label */}
        {label && (
          <label className={cn(
            "text-sm font-medium text-gray-700 font-apple",
            props.required && "required"
          )}>
            {label}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {hasLeftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {leftIcon}
            </div>
          )}

          {/* Input Field */}
          <input
            ref={ref}
            type={inputType}
            className={cn(
              baseInputStyles,
              inputVariants[variant],
              inputSizes[size],
              hasLeftIcon && 'pl-10',
              hasRightIcon && 'pr-10',
              hasError && [
                'border-apple-red focus:border-apple-red focus:ring-apple-red',
                variant === 'filled' && 'bg-red-50',
              ],
              hasSuccess && [
                'border-apple-green focus:border-apple-green focus:ring-apple-green',
                variant === 'filled' && 'bg-green-50',
              ],
              className
            )}
            {...props}
          />

          {/* Right Icons */}
          {hasRightIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              {/* Error Icon */}
              {hasError && (
                <AlertCircle className="w-4 h-4 text-apple-red" />
              )}

              {/* Success Icon */}
              {hasSuccess && !hasError && (
                <CheckCircle className="w-4 h-4 text-apple-green" />
              )}

              {/* Password Toggle */}
              {isPassword && (
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {isPasswordVisible ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Custom Right Icon */}
              {rightIcon && !hasError && !hasSuccess && (
                <span className="text-gray-400">{rightIcon}</span>
              )}
            </div>
          )}
        </div>

        {/* Helper Text, Error, or Success Message - Overlay style */}
        {(error || success || helperText) && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute top-full left-0 right-0 mt-1 z-10"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 shadow-apple-sm">
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}
            {success && !error && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 shadow-apple-sm">
                <p className="text-sm text-green-600 font-medium">{success}</p>
              </div>
            )}
            {helperText && !error && !success && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-apple-sm">
                <p className="text-sm text-gray-600">{helperText}</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  success?: string;
  helperText?: string;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  fullWidth?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      success,
      helperText,
      resize = 'vertical',
      fullWidth = true,
      className,
      ...props
    },
    ref
  ) => {
    const hasError = Boolean(error);
    const hasSuccess = Boolean(success);

    return (
      <div className={cn('flex flex-col space-y-1', !fullWidth && 'w-auto')}>
        {/* Label */}
        {label && (
          <label className="text-sm font-medium text-gray-700 font-apple">
            {label}
          </label>
        )}

        {/* Textarea */}
        <textarea
          ref={ref}
          className={cn(
            baseInputStyles,
            inputVariants.default,
            'min-h-[80px] py-3 text-base',
            resize === 'none' && 'resize-none',
            resize === 'vertical' && 'resize-y',
            resize === 'horizontal' && 'resize-x',
            resize === 'both' && 'resize',
            hasError && [
              'border-apple-red focus:border-apple-red focus:ring-apple-red',
            ],
            hasSuccess && [
              'border-apple-green focus:border-apple-green focus:ring-apple-green',
            ],
            className
          )}
          {...props}
        />

        {/* Helper Text, Error, or Success Message */}
        {(error || success || helperText) && (
          <div className="text-sm">
            {error && (
              <p className="text-apple-red font-medium">{error}</p>
            )}
            {success && !error && (
              <p className="text-apple-green font-medium">{success}</p>
            )}
            {helperText && !error && !success && (
              <p className="text-gray-500">{helperText}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'type'> {
  onSearch?: (value: string) => void;
  onClear?: () => void;
  showClearButton?: boolean;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  onClear,
  showClearButton = true,
  placeholder = 'Search...',
  ...props
}) => {
  const [value, setValue] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onSearch?.(newValue);
  };

  const handleClear = () => {
    setValue('');
    onClear?.();
  };

  return (
    <Input
      type="search"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      leftIcon={<Search className="w-4 h-4" />}
      rightIcon={
        showClearButton && value ? (
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X className="w-4 h-4" />
          </button>
        ) : undefined
      }
      {...props}
    />
  );
};

export interface PinInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

export const PinInput: React.FC<PinInputProps> = () => {
  return <div className="text-gray-500">PinInput component - Coming soon</div>;
};

export interface NumberInputProps extends Omit<InputProps, 'type'> {
  min?: number;
  max?: number;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (props, ref) => {
    return <Input ref={ref} type="number" {...props} />;
  }
);

NumberInput.displayName = 'NumberInput';

export interface InputGroupProps {
  children: React.ReactNode;
}

export const InputGroup: React.FC<InputGroupProps> = ({ children }) => {
  return <div className="flex">{children}</div>;
};

export interface InputAddonProps {
  children: React.ReactNode;
}

export const InputLeftAddon: React.FC<InputAddonProps> = ({ children }) => {
  return <div className="px-3 py-2 bg-gray-100 border border-r-0 rounded-l-md">{children}</div>;
};

export const InputRightAddon: React.FC<InputAddonProps> = ({ children }) => {
  return <div className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r-md">{children}</div>;
};

export default Input;
